import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useState, useRef } from 'react'
import { isEqual } from 'lodash-es'
import { useUiStateStore } from '../../../store/uiState/UiStateStore'
import { Box, Grid } from '@mui/material'
import styled from 'styled-components'
import BaseDrawer from '../../../ui-components/modals/BaseDrawer'
import { Translate } from 'common'
import { LaborItem } from '../../../store/models/LaborModel'
import { Button } from '../../../ui-components/forms/Button'
import { defaultTheme } from '../../../theme/index'
import { useNavigate } from 'react-router-dom'
import LaborResultBody from './LaborResultBody'
import LaborResultHeader from './LaborResultHeader'
import { useLaborLookup } from '../../../store/labor/LaborStore'
import { useSearchStore } from '../../../store/search/SearchStore'
import { breakpoints } from '../../../theme/breakpoints'
import { isMobile } from 'react-device-detect'
import LaborResultBodyMobile from './LaborResultBodyMobile'
import { cartSlice, getVehicleLabor } from 'cart'
import { RootState, store } from 'store'
import { toJS } from 'mobx'
import { useSelector } from 'react-redux'
import { show } from '@ebay/nice-modal-react'
import LaborLookupDrawer from '../lookup/LookupDrawer'

interface LaborResultDrawerProps {
  open: boolean
  setOpen: (open: boolean) => void
}

const LaborResultDrawer = observer(
  ({ open, setOpen }: LaborResultDrawerProps): ReactElement => {
    const navigate = useNavigate()
    const { displaySuccessNotification } = useUiStateStore()
    const { fetchLaborResult } = useLaborLookup()
    const { currentVehicle } = useSearchStore()
    const [selectedLaborResults, setSelectedLaborResults] = useState<
      LaborItem[]
    >([])

    const cartLaborResults = useSelector((state: RootState) =>
      getVehicleLabor(state, currentVehicle)
    )

    const prevCartLaborResultsRef = useRef<LaborItem[] | undefined>(undefined)

    useEffect(() => {
      if (open) {
        fetchLaborResult()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    useEffect(() => {
      const hasChanged = !isEqual(
        cartLaborResults?.map((item) => item.laborId),
        prevCartLaborResultsRef.current?.map((item) => item.laborId)
      )

      if (hasChanged) {
        prevCartLaborResultsRef.current = cartLaborResults
        if (cartLaborResults && cartLaborResults.length > 0) {
          setSelectedLaborResults(cartLaborResults)
        } else {
          setSelectedLaborResults([])
        }
      }
    }, [cartLaborResults])

    const handleOnClose = () => {
      if (document.querySelector('.modal.show')) return
      setOpen(false)
    }

    const specificationUpdateText = Translate('laborsUpdated')
    const viewCartLink = {
      text: Translate('viewCart'),
      action: () => {
        navigate('/cart')
      },
    }

    const handleAddToCart = () => {
      const laborItems = selectedLaborResults
      setSelectedLaborResults([])
      store.dispatch(
        cartSlice.actions.setVehicleLaborData({
          vehicle: toJS(currentVehicle),
          laborItems: toJS(laborItems),
        })
      )
      displaySuccessNotification(specificationUpdateText, viewCartLink)
      setOpen(false)
    }

    const handleOnBackClick = () => {
      setOpen(false)
      show(LaborLookupDrawer, {
        vehicle: currentVehicle,
        setLaborResult: setOpen,
      })
    }

    const drawerBody = isMobile ? (
      <LaborResultBodyMobile
        onLaborResultChange={(laborResults) => {
          setSelectedLaborResults(laborResults)
        }}
        selectedLaborResults={selectedLaborResults}
      />
    ) : (
      <LaborResultBody
        onLaborResultChange={(laborResults) => {
          setSelectedLaborResults(laborResults)
        }}
        selectedLaborResults={selectedLaborResults}
      />
    )
    const updateAddButton =
      cartLaborResults?.length > 0
        ? Translate('updateCart')
        : Translate('addToCart')

    const modalFooter = (
      <FooterGrid container rowSpacing={5} columnSpacing={1} className="mt-1">
        <Grid item xs={12} paddingTop={1}>
          <ButtonContainer display="flex" justifyContent="flex-end">
            <BackButton
              variant="secondary"
              text="back"
              onClick={handleOnBackClick}
            />
            <AddToCartButton
              onClick={handleAddToCart}
              text={updateAddButton}
              disabled={selectedLaborResults.length === 0}
            />
          </ButtonContainer>
        </Grid>
      </FooterGrid>
    )

    const modalHeader = <LaborResultHeader />

    return (
      <BaseDrawer
        open={open}
        title={Translate('laborData')}
        header={modalHeader}
        body={drawerBody}
        footer={modalFooter}
        onClose={handleOnClose}
        width={window.innerWidth < breakpoints.tabletXLarge ? '100%' : '1150px'}
        height={window.innerWidth < breakpoints.tabletXLarge ? '90vh' : '100%'}
        paddingX="32px"
        manageScrollLock
      />
    )
  }
)

export default LaborResultDrawer

const FooterGrid = styled(Grid)`
  position: sticky;
  background-color: ${defaultTheme.colors.white};
`
const ButtonContainer = styled(Box)`
  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
    display: flex;
    flex-direction: column;
  }
`
const BackButton = styled(Button)`
  @media (max-width: ${breakpoints.mobileLarge}px) {
    order: 2;
  }
`

const AddToCartButton = styled(Button)`
  @media (max-width: ${breakpoints.mobileLarge}px) {
    order: 1;
  }
`
